import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import type { ReactNode } from 'react';

/**
 * Hiệu ứng chuyển trang khi bấm mục ở thanh điều hướng (§4.4).
 *
 * Dùng `motion` + `AnimatePresence` thay cho `@keyframes` của CSS. Lý do đổi:
 * CSS animation chỉ chạy được lúc **vào**; trang cũ bị React tháo ngay nên
 * không có cách nào cho nó mờ đi. `AnimatePresence` giữ trang cũ lại trong DOM
 * cho tới khi animation `exit` xong, nên chuyển trang không còn bị "nháy trắng"
 * giữa hai màn.
 *
 * `mode="wait"`: trang mới **đợi** trang cũ biến mất hẳn mới vào. Với `sync`
 * (mặc định) thì hai trang cùng nằm trong `<main>` một nhịp và đẩy nhau xuống,
 * vì `<main>` là khối cuộn duy nhất của khung (§18.3 mục 6).
 *
 * Cặp easing theo khuyến nghị của Motion: `exit` dùng ease-in, `enter` dùng
 * ease-out, ghép lại thành một nhịp ease-in-out liền mạch.
 */

/** 300ms vào, 150ms ra — khớp `--ease-out-expo` mà CSS cũ dùng (§5.10). */
const VAO = 0.3;
const RA = 0.15;

/** Xấp xỉ `cubic-bezier` của `--ease-out-expo` và `--ease-in` trong `globals.css`. */
const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;
const EASE_IN = [0.4, 0, 1, 1] as const;

export function PageTransition({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  /*
   * Khối `@media (prefers-reduced-motion)` trong `globals.css` **không** chạm
   * tới Motion: Motion ghi thẳng inline style bằng JS chứ không qua CSS
   * animation. Vì vậy phải tự đọc và tự tắt ở đây.
   */
  const giamChuyenDong = useReducedMotion();

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={pathname}
        className="page-transition h-full"
        initial={giamChuyenDong ? false : { opacity: 0, y: 10 }}
        animate={{
          opacity: 1,
          y: 0,
          transition: giamChuyenDong
            ? { duration: 0 }
            : { duration: VAO, ease: EASE_OUT_EXPO },
        }}
        /*
         * Đặt `transition` **trong** từng trạng thái, không đặt ở prop chung:
         * nhờ vậy `exit` nhanh hơn và dùng ease-in (ra dứt khoát) còn `animate`
         * chậm hơn và dùng ease-out (vào mềm).
         */
        exit={{
          opacity: 0,
          y: -6,
          transition: giamChuyenDong ? { duration: 0 } : { duration: RA, ease: EASE_IN },
        }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}
